import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/api";
import type { Esquema } from "@/lib/contrato";

/**
 * **Grabar mejora** (M13, 0167): grabar es de todos (solo sesión); la lista, ver, el estado y borrar
 * (0221), del owner (`mejoras.ver` y `mejoras.gestionar`, que no tiene ningún cargo). El servidor
 * decide dónde se guarda (R2 o la carpeta local), el tope y si el video sigue (`video_url`).
 */

export type Improvement = Esquema<"Improvement">;
export type ImprovementList = Esquema<"ImprovementList">;
type ImprovementAvailability = Esquema<"ImprovementAvailability">;
export type ImprovementKind = Improvement["kind"];
export type ImprovementPriority = Improvement["priority"];
export type ImprovementStatus = Improvement["status"];

export const KIND_LABEL: Record<ImprovementKind, string> = { error: "Error", correction: "Corrección", recommendation: "Recomendación" };
export const PRIORITY_LABEL: Record<ImprovementPriority, string> = { high: "Alta", medium: "Media", low: "Baja" };
export const STATUS_LABEL: Record<ImprovementStatus, string> = { new: "Nueva", in_review: "En revisión", done: "Hecha", discarded: "Descartada" };

const KEY = ["improvements"] as const;

/** ¿Hay dónde guardar? Sin R2 en producción, el botón no aparece. Se pregunta una vez por sesión. */
export function useImprovementsAvailability() {
  return useQuery({
    queryKey: [...KEY, "availability"],
    queryFn: () => apiGet<ImprovementAvailability>("/improvements/availability"),
    staleTime: Infinity,
  });
}

/** La ficha que acompaña al archivo: va en la consulta, el archivo en el cuerpo. */
export type ImprovementDraft = {
  kind: ImprovementKind;
  title: string;
  priority: ImprovementPriority;
  note: string;
  screen_path: string;
  duration_s: number;
};

export function useCreateImprovement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ ficha, archivo }: { ficha: ImprovementDraft; archivo: Blob }) => {
      const params = new URLSearchParams({
        kind: ficha.kind,
        title: ficha.title,
        priority: ficha.priority,
        screen_path: ficha.screen_path,
        duration_s: String(ficha.duration_s),
      });
      if (ficha.note.trim()) params.set("note", ficha.note.trim());
      return apiPost<Improvement>(`/improvements?${params.toString()}`, archivo);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });
}

export type ImprovementFilters = { status?: string; kind?: string; priority?: string; recorded_by?: string };

export function useImprovements(filtros: ImprovementFilters) {
  const params = new URLSearchParams(Object.entries(filtros).filter(([, v]) => v) as [string, string][]);
  const consulta = params.toString();
  return useQuery({
    queryKey: [...KEY, "list", consulta],
    queryFn: () => apiGet<ImprovementList>(`/improvements${consulta ? `?${consulta}` : ""}`),
    // Al cambiar un filtro, la lista anterior se queda hasta que llega la nueva (sin parpadeo).
    placeholderData: (anterior) => anterior,
  });
}

export function useChangeImprovementStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ImprovementStatus }) => apiPatch<Improvement>(`/improvements/${id}`, { status }),
    onSuccess: () => void client.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });
}

/**
 * **Borrar de verdad** (0221): la fila desaparece del ERP con su contenido pesado (video o voz en R2, la
 * captura en la base). Uno por uno, en orden: si uno falla, los demás siguen y se dice cuántos no. La copia
 * del disco D no la toca el ERP. Cada clase tiene su ruta y su lista que se refresca.
 */
export type ClaseBorrable = "grabaciones" | "reportes" | "errores";
const BORRAR: Record<ClaseBorrable, { ruta: string; clave: readonly string[] }> = {
  grabaciones: { ruta: "/improvements", clave: [...KEY, "list"] },
  reportes: { ruta: "/user-reports", clave: [...KEY, "reports"] },
  errores: { ruta: "/app-errors", clave: ["app-errors"] },
};

export function useBorrar(clase: ClaseBorrable) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const fallidos: { id: string; error: unknown }[] = [];
      for (const id of ids) {
        try {
          await apiDelete<null>(`${BORRAR[clase].ruta}/${id}`);
        } catch (error) {
          fallidos.push({ id, error });
        }
      }
      return fallidos;
    },
    onSettled: () => void client.invalidateQueries({ queryKey: BORRAR[clase].clave }),
  });
}

/**
 * **«Reportar mejora»: captura y corrección puntual** (0182). Reportar es de todos (solo sesión); la lista,
 * la captura y el estado, del owner. Las cuatro clases (video, voz, captura, corrección) llevan el mismo
 * consecutivo `M-0001` (`code`).
 */
export type UserReport = Esquema<"UserReport">;
export type UserReportCreate = Esquema<"UserReportCreate">;
export type UserReportStatus = UserReport["status"];
export const REPORT_KIND_LABEL: Record<UserReport["kind"], string> = { screenshot: "Captura de pantalla", correction: "Corrección puntual" };

export function useCreateUserReport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UserReportCreate) => apiPost<UserReport>("/user-reports", body),
    onSuccess: () => void client.invalidateQueries({ queryKey: [...KEY, "reports"] }),
  });
}

export function useUserReports() {
  return useQuery({ queryKey: [...KEY, "reports"], queryFn: () => apiGet<UserReport[]>("/user-reports") });
}

export function useChangeUserReportStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: UserReportStatus }) => apiPatch<UserReport>(`/user-reports/${id}`, { status }),
    onSuccess: () => void client.invalidateQueries({ queryKey: [...KEY, "reports"] }),
  });
}

/** El archivo para el Taller (`scripts/taller/importar_reportes.py`): las cuatro clases con su consecutivo. */
export function useExportForWorkshop() {
  return useMutation({ mutationFn: () => apiGet<Esquema<"ExportFile">>("/user-reports/export") });
}

/** **El registro de errores del ERP** (0182): lo ve el owner (`mejoras.ver`) y lo marca revisado. */
export type AppError = Esquema<"AppError">;
export const ERRORS_KEY = ["app-errors"] as const;

export function useAppErrors(soloNuevos: boolean) {
  return useQuery({
    queryKey: [...ERRORS_KEY, "list", soloNuevos],
    queryFn: () => apiGet<AppError[]>(`/app-errors${soloNuevos ? "?only_new=true" : ""}`),
  });
}

/** Los nuevos, para la campanita: cada minuto, solo para quien los puede ver. */
export function useAppErrorCount(enabled: boolean) {
  return useQuery({
    queryKey: [...ERRORS_KEY, "count"],
    queryFn: () => apiGet<Esquema<"AppErrorCount">>("/app-errors/count"),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useReviewAppErrors() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) => apiPost<Esquema<"AppErrorReviewed">>("/app-errors/review", ids ? { ids } : {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ERRORS_KEY }),
  });
}
